import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// CONFIGS
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';

// INTERFACES

// SERVICES
import { SupplierApiService } from '../../../shared/services/api/supplier/supplier-api.service';
import { SupplierSkuApiService } from '../../../shared/services/api/supplier/supplier-sku-api.service';
import { ISupplierSku } from '../../interfaces/supplier-sku.interface';

@Component({
  selector: 'lum-supplier-sku-form',
  templateUrl: './supplier-sku-form.component.html',
  styleUrls: ['./supplier-sku-form.component.scss'],
})
export class SupplierSkuFormComponent implements OnInit, OnDestroy {
  form :UntypedFormGroup;
  loading :boolean = false;
  loadingSupplier :boolean = false;
  suppliers :ISupplierSku[] = [];
  suppliersPreviousList :ISupplierSku[] = [];

  private onDestroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data :any,
    private fb :UntypedFormBuilder,
    private dialogRef :MatDialogRef<SupplierSkuFormComponent>,
    private snackBar :MatSnackBar,
    private supplierApiService :SupplierApiService,
    private supplierSkuApiService :SupplierSkuApiService,
  ) {}

  ngOnInit() :void {
    this.getSuppliers();
    this.initForm();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onClose() :void {
    this.dialogRef.close();
  }

  onSearchSupplierChange(keyword :string) :void {
    if (keyword === '') {
      this.suppliers = this.suppliersPreviousList;
    } else {
      const lowerCaseKeyword = keyword.toLowerCase();
      this.suppliers = this.suppliers.filter((item :any) =>
        item.factory_name.toLowerCase().includes(lowerCaseKeyword),
      );
    }
  }

  onSelectSupplier(supplier :ISupplierSku) :void {
    this.supplierIdControl.setValue(supplier.id);
  }

  onSubmit() :void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    this.loading = true;

    if (this.isUpdate) {
      this.supplierSkuApiService
        .update(this.form.value.id, {
          ...this.form.value,
          supplierId: this.supplierIdControl.value?.id,
        })
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => (this.loading = false)),
        )
        .subscribe({
          next: () => {
            this.snackBar.open('Supplier SKU successfully created.', 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.dialogRef.close(true);
          },
          error: (err) => {
            this.snackBar.open(err.error, 'Error', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
        });
    } else {
      this.supplierSkuApiService
        .create({
          ...this.form.value,
          supplierId: this.supplierIdControl.value?.id,
        })
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => (this.loading = false)),
        )
        .subscribe({
          next: () => {
            this.snackBar.open('Supplier SKU successfully created.', 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.dialogRef.close(true);
          },
          error: (err) => {
            this.snackBar.open(err.error, 'Error', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
        });
    }
  }

  private initForm() :void {
    const { rfq, supplierSku } = this.data;
    const { id: rfqId } = rfq || {};
    const { productId, supplier, sku } = supplierSku || {};

    if (supplier) {
      supplier.factory_name = supplier.factoryName;
    }

    this.form = this.fb.group({
      id: [supplierSku?.id || null],
      productId: [rfqId || productId || null, Validators.required],
      supplierId: [supplier || '', Validators.required],
      sku: [sku || '', Validators.required],
    });
  }

  private getSuppliers() :void {
    this.loadingSupplier = true;
    this.supplierApiService
      .get({})
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.loadingSupplier = false)),
      )
      .subscribe({
        next: (response) => {
          const { records } = response;
          this.suppliers = records;
          this.suppliersPreviousList = records;
        },
      });
  }

  get supplierIdControl() :UntypedFormControl {
    return this.form.get('supplierId') as UntypedFormControl;
  }

  get skuControl() :UntypedFormControl {
    return this.form.get('sku') as UntypedFormControl;
  }

  get isUpdate() :boolean {
    return this.data.supplierSku !== undefined;
  }
}
